"use client";

import {
  X, Mail, Phone, MapPin, MessageCircle, ShieldCheck, CheckCircle2, XCircle,
  Landmark, Wallet, TrendingUp, Clock, FileText, PhoneCall, Bot,
} from "lucide-react";
import {
  segmentMeta, rmById, branchById, maskPan, formatINRCompact,
  type BrokingLead, type CrmRole, type ActivityType,
} from "@/lib/demos/stockBrokingData";

interface StockBrokingDetailModalProps {
  lead: BrokingLead;
  role: CrmRole;
  onClose: () => void;
  onOpenChat: (lead: BrokingLead) => void;
}

const ACTIVITY_ICON: Record<ActivityType, typeof Clock> = {
  system: Bot,
  stage: TrendingUp,
  kyc: ShieldCheck,
  call: PhoneCall,
  whatsapp: MessageCircle,
  note: FileText,
  trade: TrendingUp,
};

const ACTIVITY_COLOR: Record<ActivityType, string> = {
  system: "bg-slate-500/15 text-slate-600",
  stage: "bg-blue-500/15 text-blue-700",
  kyc: "bg-amber-500/15 text-amber-700",
  call: "bg-cyan-500/15 text-cyan-700",
  whatsapp: "bg-emerald-500/15 text-emerald-700",
  note: "bg-violet-500/15 text-violet-700",
  trade: "bg-teal-500/15 text-teal-700",
};

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-slate-600" />}
      <span className={ok ? "text-slate-700" : "text-slate-500"}>{label}</span>
    </div>
  );
}

export function StockBrokingDetailModal({ lead, role, onClose, onOpenChat }: StockBrokingDetailModalProps) {
  const rm = rmById(lead.rm_id);
  const branch = branchById(lead.branch_id);

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-emerald-950/40 my-8">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 rounded-t-2xl px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                {lead.client_code}
              </span>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{lead.stage}</span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mt-1.5">{lead.name}</h3>
            <p className="text-xs text-slate-500">
              PAN <span className="font-mono text-slate-600">{maskPan(lead.pan, role)}</span> · {branch?.name} · RM {rm?.name ?? "—"}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onOpenChat(lead)}
              className="p-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
              title="Open WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
            <a
              href={`tel:${lead.phone.replace(/\s/g, "")}`}
              className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-emerald-600 transition-colors"
              title="Click to call"
            >
              <Phone className="w-4 h-4" />
            </a>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Money tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><Wallet className="w-3 h-3" /> AUM</span>
              <div className="text-sm font-bold text-slate-900 mt-1">{formatINRCompact(lead.aum)}</div>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><Landmark className="w-3 h-3" /> Margin</span>
              <div className="text-sm font-bold text-slate-900 mt-1">{formatINRCompact(lead.margin_available)}</div>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Brokerage MTD</span>
              <div className="text-sm font-bold text-emerald-600 mt-1">{formatINRCompact(lead.brokerage_mtd)}</div>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> SIP</span>
              <div className="text-sm font-bold text-teal-700 mt-1">{lead.sip_active ? `${formatINRCompact(lead.sip_amount)}/mo` : "—"}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* KYC checklist */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <h4 className="text-[11px] font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Onboarding &amp; KYC
              </h4>
              <div className="grid grid-cols-2 gap-y-2 gap-x-3">
                <Check ok={lead.kyc_status !== "Pending"} label="PAN verified" />
                <Check ok={lead.aadhaar_linked} label="Aadhaar e-KYC" />
                <Check ok={lead.ipv_done} label="IPV done" />
                <Check ok={lead.bank_verified} label="Bank verified" />
                <Check ok={lead.nominee_added} label="Nominee added" />
                <Check ok={lead.esign_done} label="AOF e-Signed" />
              </div>
              <div className="mt-3 pt-2 border-t border-slate-200 text-[11px] text-slate-500">
                Demat: <span className="font-mono text-slate-700">{lead.demat_no || "Not allotted"}</span>
              </div>
            </div>

            {/* Contact + segments */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2.5">
              <h4 className="text-[11px] font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-emerald-600" /> Contact &amp; Segments
              </h4>
              <div className="flex items-center gap-2 text-[11px] text-slate-600"><Mail className="w-3.5 h-3.5 text-slate-500" />{lead.email}</div>
              <div className="flex items-center gap-2 text-[11px] text-slate-600"><Phone className="w-3.5 h-3.5 text-slate-500" />{lead.phone}</div>
              <div className="flex items-center gap-2 text-[11px] text-slate-600"><MapPin className="w-3.5 h-3.5 text-slate-500" />{lead.city} · {lead.source}</div>
              <div className="flex flex-wrap gap-1 pt-1">
                {lead.segments_active.length === 0 ? (
                  <span className="text-[10px] text-slate-500 italic">No segments active yet</span>
                ) : (
                  lead.segments_active.map((sid) => {
                    const seg = segmentMeta(sid);
                    return seg ? (
                      <span key={sid} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${seg.color}`}>{seg.short}</span>
                    ) : null;
                  })
                )}
                {lead.segments_requested.map((sid) => {
                  const seg = segmentMeta(sid);
                  return seg ? (
                    <span key={`req-${sid}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-dashed border-amber-500/40 text-amber-700">
                      {seg.short} (requested)
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-[11px] text-slate-600 leading-relaxed">
            <span className="text-amber-700 font-semibold">RM note: </span>{lead.notes}
          </div>

          {/* Activity timeline */}
          <div>
            <h4 className="text-[11px] font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-emerald-600" /> Complete Activity History
            </h4>
            <div className="relative pl-2">
              <div className="absolute left-[15px] top-1 bottom-1 w-px bg-slate-100" />
              <div className="space-y-3">
                {lead.timeline.map((ev) => {
                  const Icon = ACTIVITY_ICON[ev.type];
                  return (
                    <div key={ev.id} className="relative flex gap-3">
                      <div className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${ACTIVITY_COLOR[ev.type]}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 pb-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-slate-900">{ev.title}</span>
                          <span className="text-[10px] text-slate-500 shrink-0">{ev.ts}</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{ev.detail}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">— {ev.actor}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 rounded-b-2xl px-6 py-3 flex items-center justify-between">
          <span className="text-[10px] text-slate-500 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Access as <strong className="text-slate-600">{role}</strong> · PAN {role === "Admin" ? "unmasked" : "masked"}
          </span>
          <button onClick={onClose} className="py-2 px-5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
